import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import { trackButtonClick, trackTripAction } from '../../../utils/googleAnalyticsService';

const ActionButtons = ({ 
  onConfirmTrip, 
  onModifyRequest, 
  onSaveForLater, 
  onShareTrip,
  onEmailPDF,
  isConfirmDisabled, 
  isLoading,
  isAuthenticated,
  acknowledgments,
  onAcknowledgmentChange
}) => {
  const handleShareTrip = () => {
    // Track share trip action
    trackButtonClick('share_trip', 'Share Trip');
    trackTripAction('share', '', {
      source: 'trip_confirmation',
    });
    
    onShareTrip();
  };

  const handleModifyTrip = () => {
    // Track modify trip action
    trackButtonClick('modify_trip', 'Modify Trip');
    trackTripAction('modify', '', {
      source: 'trip_confirmation',
    });
    
    onModifyRequest();
  };

  const handleEmailPDF = () => {
    // Track email PDF action
    trackButtonClick('email_pdf', 'Email PDF Summary');
    trackTripAction('email_pdf', '', {
      source: 'trip_confirmation',
    });
    
    onEmailPDF();
  };

  return (
    <div className="bg-card border border-border rounded-organic p-6 shadow-organic space-y-6">
      {/* Combined Section Title */}
      <div className="flex items-center space-x-2">
        <Icon name="Shield" size={20} color="var(--color-primary)" />
        <h3 className="font-heading font-heading-bold text-lg text-card-foreground">
          Important Acknowledgments & Next Steps
        </h3>
      </div>

      {/* Acknowledgments Checkboxes */}
      <div className="space-y-4">
        <Checkbox
          label="I understand and agree to follow all park regulations and rules"
          description="This includes camping guidelines, fire restrictions, and wildlife safety protocols"
          checked={acknowledgments?.parkRegulations}
          onChange={(e) => onAcknowledgmentChange('parkRegulations', e?.target?.checked)}
          className="mb-3"
        />

        <Checkbox
          label="I acknowledge the weather conditions and will prepare accordingly"
          description="Weather can change rapidly in outdoor environments. Pack appropriate gear for all conditions"
          checked={acknowledgments?.weatherConditions}
          onChange={(e) => onAcknowledgmentChange('weatherConditions', e?.target?.checked)}
          className="mb-3"
        />

        <Checkbox
          label="I understand that some activities may require advance reservations"
          description="Popular campsites, guided tours, and facilities may need to be booked separately"
          checked={acknowledgments?.reservationRequirements}
          onChange={(e) => onAcknowledgmentChange('reservationRequirements', e?.target?.checked)}
          className="mb-3"
        />

        <Checkbox
          label="I accept responsibility for my safety and the safety of my group"
          description="Outdoor activities carry inherent risks. Always inform someone of your plans and expected return"
          checked={acknowledgments?.safetyResponsibility}
          onChange={(e) => onAcknowledgmentChange('safetyResponsibility', e?.target?.checked)}
        />
      </div>

      {/* Emergency Preparedness Warning */}
      <div className="p-4 bg-warning/10 border border-warning/20 rounded-organic">
        <div className="flex items-start space-x-2">
          <Icon name="AlertTriangle" size={16} color="var(--color-warning)" className="mt-0.5" />
          <div>
            <p className="font-body font-body-medium text-sm text-warning mb-1">Emergency Preparedness</p>
            <p className="font-caption text-xs text-muted-foreground">
              Always carry emergency supplies, inform others of your itinerary, and check park alerts before departure. 
              Cell service may be limited in remote areas.
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        {/* Primary Action - Confirm Trip */}
        <Button
          variant="default"
          onClick={onConfirmTrip}
          disabled={isConfirmDisabled || isLoading}
          iconName={isLoading ? "Loader" : "Check"}
          iconPosition="left"
          className="w-full justify-center text-sm py-3"
        >
          {isLoading ? 'Processing...' : (isAuthenticated ? 'Confirm & Save Trip' : 'Confirm Trip')}
        </Button>

        {/* Email PDF Option */}
        <Button
          variant="outline"
          onClick={handleEmailPDF}
          iconName="Mail"
          iconPosition="left"
          className="w-full justify-center text-sm py-2"
        >
          Email PDF Summary
        </Button>

        {/* Secondary Actions Row */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="ghost"
            onClick={handleModifyTrip}
            iconName="Edit"
            iconPosition="left"
            className="justify-center text-sm"
          >
            Modify Trip
          </Button>
          
          {isAuthenticated ? (
            <Button
              variant="ghost"
              onClick={onSaveForLater}
              iconName="Bookmark"
              iconPosition="left"
              className="justify-center text-sm"
            >
              Save Draft
            </Button>
          ) : (
            <Button
              variant="ghost"
              onClick={handleShareTrip}
              iconName="Share"
              iconPosition="left"
              className="justify-center text-sm"
            >
              Share Link
            </Button>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-4 p-4 bg-muted/30 rounded-organic">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={16} color="var(--color-primary)" className="mt-0.5" />
            <div className="text-xs text-muted-foreground">
              {isAuthenticated ? (
                <p>
                  <strong>Logged In:</strong> Your trip will be saved to your account and accessible from your dashboard anytime.
                </p>
              ) : (
                <p>
                  <strong>Not Logged In:</strong> You can email a PDF copy or create an account to save this trip permanently. 
                  Creating an account lets you access your trips anytime and get personalized recommendations.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionButtons;